import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, fetchSession, login, logout } from './lib/api';
import type { SessionState, UploadResult, UserId } from './types';
import { USER_LABELS } from './types';
import { Login } from './components/Login';
import { Uploader } from './components/Uploader';
import { Gallery } from './components/Gallery';
import { Spinner } from './components/Spinner';
import { ToastStack, type ToastMessage } from './components/Toast';

type Phase = 'loading' | 'login' | 'app';

const DEFAULT_USERS: UserId[] = ['kay', 'lia'];
const FALLBACK_PREFIX = 'uploads';

function lastPrefixKey(user: UserId): string {
  return `fox-image:last-prefix:${user}`;
}

function loadLastPrefix(user: UserId): string {
  try {
    const v = localStorage.getItem(lastPrefixKey(user));
    return v && v.trim() ? v.trim() : FALLBACK_PREFIX;
  } catch {
    return FALLBACK_PREFIX;
  }
}

function saveLastPrefix(user: UserId, prefix: string): void {
  try {
    localStorage.setItem(lastPrefixKey(user), prefix);
  } catch {
    // ignore quota / disabled
  }
}

export default function App() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<SessionState>({
    authenticated: false,
    configured: true,
    users: DEFAULT_USERS,
  });
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [galleryRefresh, setGalleryRefresh] = useState(0);
  const [lastPrefix, setLastPrefix] = useState<string>(FALLBACK_PREFIX);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [signingOut, setSigningOut] = useState(false);
  const toastIdRef = useRef(1);

  const pushToast = useCallback((kind: ToastMessage['kind'], text: string) => {
    const id = toastIdRef.current++;
    setToasts((prev) => [...prev, { id, kind, text }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSession()
      .then((state) => {
        if (cancelled) return;
        const merged: SessionState = {
          ...state,
          users: state.users && state.users.length > 0 ? state.users : DEFAULT_USERS,
        };
        setSession(merged);
        setPhase(merged.authenticated ? 'app' : 'login');
      })
      .catch(() => {
        if (cancelled) return;
        setSession({ authenticated: false, configured: true, users: DEFAULT_USERS });
        setPhase('login');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the user is known, hydrate the per-user "last prefix used".
  useEffect(() => {
    if (phase === 'app' && session.user) setLastPrefix(loadLastPrefix(session.user));
  }, [phase, session.user]);

  const handleLogin = useCallback(
    async (user: UserId, credential: string) => {
      try {
        const res = await login(user, credential);
        setSession((prev) => ({
          ...prev,
          authenticated: true,
          configured: true,
          user: res.user,
          expiresAt: res.expiresAt,
        }));
        setPhase('app');
        setActiveFolder(null);
        pushToast('success', `Signed in as ${USER_LABELS[res.user]}.`);
      } catch (err) {
        if (err instanceof ApiError) throw new Error(err.message);
        throw err;
      }
    },
    [pushToast],
  );

  const handleLogout = useCallback(async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      // best-effort — clear cookie locally regardless
    } finally {
      setSession((prev) => ({
        authenticated: false,
        configured: prev.configured,
        users: prev.users,
      }));
      setActiveFolder(null);
      setPhase('login');
      setSigningOut(false);
      pushToast('info', 'Signed out.');
    }
  }, [pushToast]);

  // Per-file completion: remember the prefix for next time. We don't refresh
  // the gallery here — that happens once at end-of-batch to avoid hammering
  // the manifest re-fetch on every concurrent completion.
  const handleItemUploaded = useCallback(
    (result: UploadResult) => {
      if (session.user) {
        saveLastPrefix(session.user, result.prefix);
        setLastPrefix(result.prefix);
      }
    },
    [session.user],
  );

  const handleBatchComplete = useCallback(
    ({ ok, failed }: { ok: number; failed: number }) => {
      setGalleryRefresh((n) => n + 1);
      if (ok > 0 && failed === 0) {
        pushToast('success', `Uploaded ${ok} file${ok === 1 ? '' : 's'}.`);
      } else if (ok > 0 && failed > 0) {
        pushToast('info', `Uploaded ${ok}, failed ${failed}.`);
      } else if (ok === 0 && failed > 0) {
        // per-file errors already toasted by Uploader → onError; skip summary
      }
    },
    [pushToast],
  );

  const handleUploadError = useCallback(
    (message: string) => {
      pushToast('error', message);
    },
    [pushToast],
  );

  const handleCopied = useCallback(
    (label: string) => {
      if (label) pushToast('info', `${label} copied.`);
    },
    [pushToast],
  );

  if (phase === 'loading') {
    return (
      <div className="grid min-h-[100dvh] place-items-center text-muted">
        <div className="flex items-center gap-2">
          <Spinner size={18} />
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  if (phase === 'login') {
    return (
      <>
        <Login configured={session.configured} availableUsers={session.users} onSubmit={handleLogin} />
        <ToastStack toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  const username = session.user ? USER_LABELS[session.user] : '';
  const uploaderDefaultPrefix = activeFolder ?? lastPrefix;

  return (
    <>
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6 xl:max-w-6xl 2xl:max-w-[1400px] min-[1700px]:max-w-[1620px] min-[1920px]:max-w-[1820px]">
        <header className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div
              aria-hidden="true"
              className="grid h-9 w-9 place-items-center rounded-lg"
              style={{
                background: 'linear-gradient(135deg, var(--color-app-accent), var(--color-app-accent-2))',
              }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="#0b0b0f">
                <path d="M5 17 9 9l4 5 3-3 4 6Z" />
                <circle cx="16" cy="6" r="2" />
              </svg>
            </div>
            <div>
              <h1 className="text-base font-semibold leading-none">fox-image</h1>
              <p className="text-xs text-muted">
                Signed in as <span className="text-app">{username}</span>
              </p>
            </div>
          </div>
          <button type="button" className="btn btn-ghost text-xs" onClick={handleLogout} disabled={signingOut}>
            {signingOut ? <Spinner size={12} /> : null}
            <span>Sign out</span>
          </button>
        </header>

        <Uploader
          defaultPrefix={uploaderDefaultPrefix}
          onItemUploaded={handleItemUploaded}
          onBatchComplete={handleBatchComplete}
          onError={handleUploadError}
        />

        {session.user && (
          <Gallery
            user={session.user}
            refreshKey={galleryRefresh}
            activeFolder={activeFolder}
            onActiveFolderChange={setActiveFolder}
            onError={handleUploadError}
            onCopied={handleCopied}
          />
        )}

        <footer className="pt-2 text-center text-xs text-muted">
          <span>fox-image</span>
          <span className="mx-2">·</span>
          <a
            className="underline-offset-2 hover:underline"
            href="https://developers.cloudflare.com/r2/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cloudflare R2
          </a>
        </footer>
      </div>
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );
}
